<template>
  <div class="search-container" :class="{ 'dark-mode': isDarkMode }" role="search">
    <label class="sr-only" :for="inputId">Iboralarni qidirish</label>
    <div class="search-field" :class="{ 'is-focused': isFocused }">
      <Search class="search-icon" :size="20" :stroke-width="2" aria-hidden="true" />
      <input
        :id="inputId"
        ref="searchInput"
        v-model="query"
        type="search"
        class="search-input"
        placeholder="Qidiruv"
        autocomplete="off"
        autocapitalize="none"
        spellcheck="false"
        role="combobox"
        aria-label="Iboralarni qidirish"
        :aria-expanded="hasPanel ? 'true' : 'false'"
        :aria-controls="resultsId"
        :aria-activedescendant="activeDescendant"
        @focus="isFocused = true"
        @blur="handleBlur"
        @input="onInputChange"
        @keydown.down.prevent="moveActive(1)"
        @keydown.up.prevent="moveActive(-1)"
        @keydown.enter.prevent="selectActive"
        @keydown.esc.prevent="closePanel"
      />
      <span v-if="isLoading" class="loading-indicator" aria-label="Qidirilmoqda"></span>
      <button
        v-if="query.length > 0"
        type="button"
        class="clear-button"
        @mousedown.prevent
        @click="clearSearch"
        aria-label="Qidiruvni tozalash"
      >
        <X :size="20" :stroke-width="2" aria-hidden="true" />
      </button>
    </div>

    <div
      v-if="isFocused && query.length > 0 && filteredResults.length > 0"
      :id="resultsId"
      class="search-panel"
      role="listbox"
      aria-label="Qidiruv natijalari"
    >
      <button
        v-for="(result, index) in filteredResults"
        :id="resultOptionId(index)"
        :key="result.id || result.slug || index"
        type="button"
        class="result-option"
        :class="{ active: activeIndex === index }"
        role="option"
        :aria-selected="activeIndex === index ? 'true' : 'false'"
        @mousedown.prevent
        @click="goToCard(result)"
      >
        <span class="result-title">{{ result.title }}</span>
      </button>
    </div>

    <div
      v-else-if="isFocused && query.length > 0 && filteredResults.length === 0 && !isLoading"
      :id="resultsId"
      class="search-panel empty"
      role="status"
    >
      Hech qanday ibora topilmadi.
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';
import { Search, X } from 'lucide-vue-next';
import { mapGetters } from 'vuex';

export default {
  name: 'SearchBar',
  components: {
    Search,
    X,
  },
  data() {
    const uid = Math.random().toString(36).slice(2, 9);

    return {
      activeIndex: -1,
      filteredResults: [],
      inputId: `site-search-${uid}`,
      isFocused: false,
      isLoading: false,
      query: '',
      resultsId: `site-search-results-${uid}`,
    };
  },
  computed: {
    ...mapGetters(['isDarkMode']),
    hasPanel() {
      return this.isFocused && this.query.length > 0;
    },
    activeDescendant() {
      return this.activeIndex >= 0 ? this.resultOptionId(this.activeIndex) : undefined;
    },
  },
  watch: {
    filteredResults() {
      this.activeIndex = this.filteredResults.length > 0 ? 0 : -1;
    },
    query(value) {
      if (value.length < 3) {
        this.filteredResults = [];
      }
    },
  },
  methods: {
    async fetchSearchResults() {
      if (this.query.length < 3) {
        this.filteredResults = [];
        return;
      }

      const url = `phrases/phrases/?search=${encodeURIComponent(this.query)}`;
      this.isLoading = true;

      try {
        const token = localStorage.getItem('auth_token');
        const requestConfig = token
          ? {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
          : {};

        const response = await axios.get(url, requestConfig);

        this.filteredResults = Array.isArray(response.data.results)
          ? response.data.results
          : Array.isArray(response.data)
            ? response.data
            : [];
      } catch (error) {
        this.filteredResults = [];
      } finally {
        this.isLoading = false;
      }
    },
    onInputChange: debounce(function () {
      this.fetchSearchResults();
    }, 300),
    clearSearch() {
      this.query = '';
      this.filteredResults = [];
      this.activeIndex = -1;
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    closePanel() {
      this.filteredResults = [];
      this.activeIndex = -1;
      this.isFocused = false;
      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur();
      }
    },
    handleBlur() {
      window.setTimeout(() => {
        this.isFocused = false;
        this.activeIndex = -1;
      }, 120);
    },
    moveActive(direction) {
      if (!this.filteredResults.length) return;

      const lastIndex = this.filteredResults.length - 1;
      if (this.activeIndex < 0) {
        this.activeIndex = direction > 0 ? 0 : lastIndex;
        return;
      }

      this.activeIndex = (this.activeIndex + direction + this.filteredResults.length) % this.filteredResults.length;
    },
    selectActive() {
      if (this.activeIndex < 0 || !this.filteredResults[this.activeIndex]) return;
      this.goToCard(this.filteredResults[this.activeIndex]);
    },
    resultOptionId(index) {
      return `${this.resultsId}-option-${index}`;
    },
    goToCard(result) {
      if (!result || !result.slug) {
        return;
      }

      this.$router.push({ name: 'PhraseDetail', params: { slug: result.slug } }).catch(() => {});
      this.filteredResults = [];
      this.query = '';
      this.activeIndex = -1;
      this.isFocused = false;
    },
  },
};
</script>

<style scoped>
.search-container {
  position: relative;
  width: min(100%, 380px);
  color: var(--md-sys-color-on-surface);
}

.search-field {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 0 6px 0 16px;
  border: 1px solid transparent;
  border-radius: var(--md-sys-shape-corner-full);
  background: var(--md-sys-color-surface-container-high);
  transition:
    background-color var(--md-sys-motion-standard),
    border-color var(--md-sys-motion-standard),
    box-shadow var(--md-sys-motion-standard);
}

.search-field.is-focused {
  background: var(--md-sys-color-surface-container-highest);
  border-color: var(--md-sys-color-primary);
  box-shadow: var(--md-focus-ring);
}

.search-icon {
  flex: 0 0 auto;
  margin-right: 12px;
  color: var(--md-sys-color-on-surface-variant);
}

.search-input {
  flex: 1;
  min-width: 0;
  height: 46px;
  padding: 0;
  border: 0;
  color: var(--md-sys-color-on-surface);
  background: transparent;
  caret-color: var(--md-sys-color-primary);
  font: var(--md-sys-typescale-body-large);
}

.search-input:focus {
  outline: none;
}

.search-input::-webkit-search-decoration,
.search-input::-webkit-search-cancel-button {
  display: none;
}

.search-input::placeholder {
  color: var(--md-sys-color-on-surface-variant);
  opacity: 1;
}

.clear-button {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-right: -6px;
  overflow: hidden;
  border: 0;
  border-radius: var(--md-sys-shape-corner-full);
  color: var(--md-sys-color-on-surface-variant);
  background: transparent;
  cursor: pointer;
}

.clear-button::before,
.result-option::before {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  content: "";
  opacity: 0;
  transition: opacity var(--md-sys-motion-short);
}

.clear-button:hover::before,
.result-option:hover::before {
  opacity: var(--md-sys-state-hover-opacity);
}

.clear-button:active::before,
.result-option:active::before {
  opacity: var(--md-sys-state-pressed-opacity);
}

.clear-button svg {
  position: relative;
}

.loading-indicator {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin: 0 10px;
  border: 2px solid var(--md-sys-color-outline-variant);
  border-top-color: var(--md-sys-color-primary);
  border-radius: var(--md-sys-shape-corner-full);
  animation: search-spin 800ms linear infinite;
}

.search-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1001;
  width: 100%;
  max-height: min(360px, calc(100vh - 144px));
  padding: 8px;
  overflow-y: auto;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--md-sys-shape-corner-large);
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-sys-elevation-3);
}

.search-panel.empty {
  min-height: 56px;
  padding: 16px;
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-body-medium);
}

.result-option {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  overflow: hidden;
  border: 0;
  border-radius: var(--md-sys-shape-corner-small);
  color: var(--md-sys-color-on-surface);
  background: transparent;
  cursor: pointer;
  font: var(--md-sys-typescale-body-medium);
  text-align: left;
}

.result-option.active {
  background: color-mix(in srgb, var(--md-sys-color-primary) 12%, transparent);
}

.result-title {
  position: relative;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

@keyframes search-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 820px) {
  .search-container {
    width: 100%;
  }

  .search-panel {
    position: fixed;
    top: 72px;
    right: var(--app-page-padding);
    left: var(--app-page-padding);
    width: auto;
    max-height: calc(100vh - 176px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .loading-indicator {
    animation: none;
  }

  .search-field,
  .clear-button::before,
  .result-option::before {
    transition: none;
  }
}
</style>
